import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FormService, LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {Place} from '../../shared/models/place';
import {ApiService} from '../../shared/services/api.service';
import {UserPermission, UserPlatformRole} from '../../shared/models/user';
import {SyncService} from 'src/app/shared/services/sync.service';
import {DialogDailyOtpComponent} from '../../components/dialogs/dialog-daily-otp/dialog-daily-otp.component';
import {DialogsService} from '../../shared/services/dialog.service';
import {PlacesService} from '../../shared/services/places.service';
import {ActivatedRoute} from '@angular/router';

declare const window: Window & {initialQueryString: string[][]};

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SignInComponent implements OnInit {
  form: FormGroup;
  submitted = false;
  connectionAd = false;
  secondStep: 'local' | 'ad';
  selectedPlace = '';
  params: {
    email: string;
    password: string;
    isMobileKiosk: boolean;
    place: string;
    webKiosk: boolean;
    access_token?: string;
  };
  places: any;
  placesSO:  {name: string, value: string}[] = [];
  internal: boolean;

  constructor(public nav: NavigateService,
              public forms: FormService,
              public api: ApiService,
              private sync: SyncService,
              private formBuilder: FormBuilder,
              private lang: LangService,
              private loader: LoaderService,
              private dialog: DialogsService,
              public place: PlacesService,
              private route: ActivatedRoute) {
    const internalHolder = window.initialQueryString.find((q: string[]) => q[0] === 'internal');
    if(this.api.isElectronApp || internalHolder) {
      this.internal = true;
    }
    this.connectionAd = this.internal;
  }

  ngOnInit(): void {
    this.setForm();
    this.route.queryParams.subscribe((params: any) => {
      const {email, password, isMobileKiosk, place, webKiosk, access_token} = params;
      this.params = {email, password, isMobileKiosk, place, webKiosk, access_token};
      if(this.params.webKiosk) {
        sessionStorage.setItem('webKiosk', 'true');
      } 
      if(place) {
        this.form.patchValue({place});
      }
      if(access_token) {
        this.loginWithAccessToken(access_token);
      } else if(email && password) {
        this.loader.loading(true);
        const isPortalConnection = isMobileKiosk === 'true';
        this.loginWeak(email, password, undefined, isPortalConnection);
      }
    });
    this.api.processedSession = null;
    localStorage.removeItem('processedSession');
  }

  setForm() {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required, ...(this.connectionAd ? [] : [Validators.email])]],
      password: ['', [Validators.required]],
      place: ['', []]
    });
  }

  submitForm() {
    this.submitted = true;
    if(this.forms.getFormErrors(this.form).length < 1) {
      this.loader.loading(true);
      this.loginWeak(undefined, undefined, this.connectionAd);
    }
  }

  private navigateAfterLogin(): void {
    if(this.api.getStoredItem('welcome') || !this.api.env.showWelcome || this.api.userInfo.role === UserPlatformRole.KIOSK) {
      if(this.params?.webKiosk) {
        this.nav.to('passport-type');
      }
      this.nav.to('dashboard');
    } else if(this.api.userRole.isCustomer) {
      this.nav.to('welcome-hello');
    }
  }

  loginWeak(email?: string, password?: string, adConnection = false, isShortJwt = false): boolean {
    const accountName = email || this.form.get('email').value;
    const checkSignInOnePlace = () => {
      if(this.placesSO.length === 1) {
        this.form.get('place').setValue(this.placesSO[0].value);
        return this.loginWeak(email, password, adConnection);
      }
    };
    this.api.consumeLoginWeak(accountName, password || this.form.get('password').value, this.form.get('place').value || this.api.userPlaceId,
      undefined, isShortJwt,  adConnection && this.secondStep !== 'local').then(([local, places, isBackupUri]) => {
      if(this.api.userPlaceId) {
        if(this.api.isOnline) {
          this.sync.syncAll();
        }
        this.leaveToSearch(isBackupUri);
      } else {
        if(!this.secondStep) {
          if(!local) {
            this.loader.loading(false);
            this.placesSO = (places as string[]).map(place => ({name: place, value: place}));
            checkSignInOnePlace();
            this.secondStep = 'ad';
            this.toggleFormControls();
          } else {
            this.api.info().then(() => {
              this.loader.loading(false);
              if(this.api.userInfo?.links?.length > 0 && !this.api.userRole.isCustomer) {
                this.place.getAllPlacesByPerson(this.api.userInfo).then(() => {
                  for(const place in this.place.allPlacesByPerson) {
                    const {longName, id} = this.place.allPlacesByPerson[place] as Place;
                    this.placesSO.push({name: longName, value: id});
                  }
                  checkSignInOnePlace();
                }).catch(err => {
                  if(err === 'err.user: client.extended.wrongAuth') {
                    this.secondStep = undefined;
                    this.loader.loading(true, {type: 'error', message: 'This account has no access permission'});
                  }
                });
                this.secondStep = 'local';
                this.toggleFormControls();
              } else {
                this.loader.loading(false);
                this.navigateAfterLogin();
              }
            }, error => {
              if(this.api.userRole && (this.api.userRole.isOrbis || this.api.userRole.isPartnerRegister)) {
                this.submitted = false;
                this.loader.loading(true, {type: 'error', message: 'This account has no access permission'});
              } else {
                this.submitted = false;
                this.loader.loading(true, {type: 'error', message: error});
              }
            });
          }
        }
      }
    }, err => {
      if(err?.includes(this.lang.transform('client.extended.clientBadOtp'))) {
        this.submitted = false;
        this.loader.loading(false);
        this.openOtp();
      } else if(err?.includes(this.lang.transform('client.extended.confirmationRequired'))) {
        this.submitted = false;
        const accountName = this.form.get('email').value;
        const msgKey = accountName?.includes('@') ? 'confirmationRequired.email' : 'confirmationRequired.sms';
        this.loader.loading(true, {type: 'warn', message: this.lang.transform(msgKey)});
      } else {
        if(err?.includes(this.lang.transform('client.extended.placeNotAllowed'))) {
          this.loader.loading(false);
          this.loginWeak(undefined, undefined, this.connectionAd);
        } else {
          this.loader.loading(true, {type: 'error', message: err});
        }
        this.submitted = false;
      }
    });
    return true;
  }

  loginWithAccessToken(accessToken: string): void {
    this.loader.loading(true);
    try {
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }
      const payload = JSON.parse(atob(tokenParts[1]));
      if (payload.exp && payload.exp <= Date.now() / 1000) {
        throw new Error('Token has expired');
      }
      this.api.jwt = accessToken;
      this.api.info().then(() => {
        if (this.api.isOnline) {
          this.sync.syncAll();
        }
        this.loader.loading(false);
        this.navigateAfterLogin();
      }).catch(error => {
        this.loader.loading(true, {type: 'error', message: 'Invalid access token or authentication failed'});
        console.error('Access token authentication failed:', error);
      });
    } catch (error) {
      this.loader.loading(true, {type: 'error', message: 'Invalid access token format'});
      console.error('Access token validation failed:', error);
    }
  }

  openOtp() {
    const accountName = this.form.get('email').value;
    const data: { email: string, password: string, placeId: string } = {
      email: accountName,
      password: this.form.get('password').value,
      placeId: this.api.userPlaceId || this.form.get('place').value
    };
    this.dialog.openDialog(DialogDailyOtpComponent, 'sm', data);
  }

  protected leaveToSearch(isBackupUri = false) {
    this.api.flexibleUri = isBackupUri ? this.api.env.backupUri : this.api.env.baseUri;
    return this.api.info().then(() => {
      this.api.flexibleUri = this.api.env.baseUri;
      this.sync.cacheUsers();
      if(this.api.userRole.isOrbis || this.api.userRole.isPartnerRegister) {
        this.loader.loading(true, {type: 'error', message: 'This account has no access permission'});
      } else {
        this.loader.loading(false);
        if(this.api.getStoredItem('welcome') || this.api.userInfo.role !== UserPlatformRole.CUSTOMER) {
          if(this.api.userRole.isExternalMonitor || (this.api.userRole.isWorker && (this.api.hasPerm(UserPermission.ALLOW_CONTROL) && !this.api.hasPerm(UserPermission.ALLOW_ADMIN) && !this.api.hasPerm(UserPermission.ALLOW_CONSUME)))) {
            this.nav.to('admin-request');
          } else {
            this.nav.to('dashboard');
          }
        } else {
          this.nav.to('welcome');
        }
      }
    }, err => {
      this.loader.loading(true, {type: 'error', message: err});
    });
  }

  selectChange(event: any) {
    this.selectedPlace = event.target.value;
  }

  cancel() {
    this.secondStep = undefined;
    this.selectedPlace = '';
    this.submitted = false;
    this.placesSO = [];
    this.api.flexibleUri = this.api.env.baseUri;
    this.toggleFormControls();
    this.setForm();
    clearInterval(this.api.pingInterval);
    this.api.pingInterval = undefined;
  }

  toggleFormControls() {
    if(this.secondStep) {
      this.form.get('email').disable();
      this.form.get('password').disable();
    } else {
      this.form.get('email').enable();
      this.form.get('password').enable();
    }
  }

  changeConnection(event: any) {
    this.connectionAd = event;
    if(this.connectionAd) {
      this.form.controls.email.removeValidators([Validators.email]);
    } else {
      this.form.controls.email.addValidators([Validators.email]);
    }
    this.form.controls.email.updateValueAndValidity();
  }
}
